import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Save, Palette, RefreshCw } from "lucide-react";
import api from "@/lib/api";
import { useBrand } from "@/contexts/BrandContext";

const FIELDS = [
  { key: "brand_name", label: "Brand name" },
  { key: "tagline", label: "Tagline" },
  { key: "secondary_line", label: "Secondary line" },
  { key: "logo_url", label: "Logo URL" },
  { key: "favicon_url", label: "Favicon URL" },
];
const COLORS = [
  { key: "primary_color", label: "Primary" },
  { key: "secondary_color", label: "Secondary" },
  { key: "accent_color", label: "Accent" },
  { key: "background_color", label: "Background" },
  { key: "text_color", label: "Text" },
];

export default function AdminBrandPage() {
  const { brand, refresh } = useBrand();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (brand) setForm(brand); }, [brand]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/brand", form);
      toast.success("Brand updated — the live site reflects the changes.");
      await refresh();
    } catch (err) { toast.error(err?.response?.data?.error || "Save failed"); } finally { setSaving(false); }
  };

  return (
    <div className="max-w-4xl" data-testid="admin-brand">
      <div className="mb-6 flex items-start justify-between gap-3">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#165a71]">Admin · Brand</div>
          <h1 className="mt-2 font-display text-2xl md:text-3xl font-semibold text-[#071b22]">Brand settings</h1>
          <p className="text-[#252A34]/70 text-sm mt-1">Editable at runtime. Saving updates the public site immediately (CSS variables + brand context).</p>
        </div>
        <button onClick={save} disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-[#165a71] text-white px-5 py-2 text-sm font-medium disabled:opacity-50" data-testid="brand-save">
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save changes"}
        </button>
      </div>

      <div className="rounded-2xl border border-slate-200 bg-white p-6 space-y-4">
        {FIELDS.map((f) => (
          <div key={f.key}>
            <label className="text-xs text-[#252A34]/70">{f.label}</label>
            <input value={form[f.key] || ""} onChange={(e) => setForm((p) => ({ ...p, [f.key]: e.target.value }))} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid={`brand-field-${f.key}`} />
          </div>
        ))}
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
        <div className="font-display font-semibold text-[#071b22] mb-3 inline-flex items-center gap-2"><Palette className="h-4 w-4" /> Palette</div>
        <div className="grid gap-3 sm:grid-cols-2 md:grid-cols-3">
          {COLORS.map((c) => (
            <div key={c.key} className="flex items-center gap-3">
              <input type="color" value={form[c.key] || "#000000"} onChange={(e) => setForm((p) => ({ ...p, [c.key]: e.target.value }))} className="h-10 w-10 rounded-lg border border-slate-200" data-testid={`brand-color-${c.key}`} />
              <div className="flex-1">
                <div className="text-xs text-[#252A34]/70">{c.label}</div>
                <input value={form[c.key] || ""} onChange={(e) => setForm((p) => ({ ...p, [c.key]: e.target.value }))} className="w-full rounded-lg border border-slate-200 px-2 py-1 text-xs" />
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6">
        <div className="font-display font-semibold text-[#071b22] mb-3">Typography & style</div>
        <div className="grid gap-3 sm:grid-cols-2">
          {[["heading_font", "Heading font"], ["body_font", "Body font"], ["button_style", "Button style"], ["border_radius", "Border radius"], ["card_style", "Card style"]].map(([k, l]) => (
            <div key={k}>
              <label className="text-xs text-[#252A34]/70">{l}</label>
              <input value={form[k] || ""} onChange={(e) => setForm((p) => ({ ...p, [k]: e.target.value }))} className="mt-1 w-full rounded-xl border border-slate-200 px-3 py-2 text-sm" data-testid={`brand-typography-${k}`} />
            </div>
          ))}
          <label className="inline-flex items-center gap-2 text-sm text-[#252A34] mt-6">
            <input type="checkbox" checked={!!form.maintenance_mode} onChange={(e) => setForm((p) => ({ ...p, maintenance_mode: e.target.checked }))} data-testid="brand-maintenance-mode" />
            Maintenance mode
          </label>
        </div>
      </div>
    </div>
  );
}
